import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, X, AlertTriangle } from "lucide-react";
import { RCA_CATEGORIES, ACTION_STATUS, toLocalInput, fromLocalInput, fmtDateTime } from "@/lib/rca";
import { Field } from "@/components/rca/RcaBits";

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

export function TagInput({ value = [], onChange, disabled, placeholder, testid }) {
  const [v, setV] = useState("");
  const add = () => { const t = v.trim(); if (t && !value.includes(t)) onChange([...value, t]); setV(""); };
  return (
    <div className="flex flex-wrap gap-1.5 items-center border border-slate-200 rounded-md p-1.5 min-h-[40px]" data-testid={testid}>
      {value.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded px-2 py-1">
          {t}{!disabled && <button onClick={() => onChange(value.filter((x) => x !== t))} aria-label={`Remove ${t}`}><X className="w-3 h-3" /></button>}
        </span>
      ))}
      {!disabled && (
        <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          onBlur={add} placeholder={placeholder} className="flex-1 min-w-[120px] text-sm outline-none px-1 bg-transparent" data-testid={`${testid}-input`} />
      )}
    </div>
  );
}

export function TimelineEditor({ value = [], onChange, disabled }) {
  const upd = (id, k, v) => onChange(value.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2" data-testid="rca-timeline">
      {value.map((x) => (
        <div key={x.id} className="flex flex-col sm:flex-row gap-2 sm:items-start" data-testid={`timeline-item-${x.id}`}>
          {disabled ? <span className="text-xs font-mono text-slate-500 sm:w-48 pt-1">{fmtDateTime(x.at)}</span> : (
            <Input type="datetime-local" value={toLocalInput(x.at)} onChange={(e) => upd(x.id, "at", fromLocalInput(e.target.value))} className="sm:w-56 h-9" data-testid="timeline-at-input" />
          )}
          {disabled ? <p className="text-sm text-slate-700 flex-1">{x.event}</p> : (
            <Input value={x.event} onChange={(e) => upd(x.id, "event", e.target.value)} placeholder="What happened (e.g. Alert fired for 5xx spike)" className="flex-1 h-9" data-testid="timeline-event-input" />
          )}
          {!disabled && <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500 shrink-0" onClick={() => onChange(value.filter((y) => y.id !== x.id))} data-testid="timeline-remove"><Trash2 className="w-4 h-4" /></Button>}
        </div>
      ))}
      {!value.length && <p className="text-sm text-slate-400">No events yet.</p>}
      {!disabled && <Button variant="outline" size="sm" onClick={() => onChange([...value, { id: newId(), at: "", event: "" }])} data-testid="timeline-add"><Plus className="w-3.5 h-3.5 mr-1" />Add event</Button>}
    </div>
  );
}

export function WhysEditor({ value = [""], onChange, disabled }) {
  const list = value.length ? value : [""];
  return (
    <div className="space-y-2" data-testid="rca-whys">
      {list.map((w, i) => (
        <div key={i} className="flex gap-2 items-start">
          <span className="text-xs font-bold text-indigo-600 w-14 pt-2.5 shrink-0">Why {i + 1}</span>
          {disabled ? <p className="text-sm text-slate-700 pt-2 flex-1">{w || "—"}</p> : (
            <Textarea value={w} onChange={(e) => onChange(list.map((x, j) => (j === i ? e.target.value : x)))} className="min-h-[40px] text-sm flex-1"
              placeholder={i === 0 ? "Why did the incident happen?" : `Why did that happen? (answer to Why ${i})`} data-testid={`why-input-${i}`} />
          )}
          {!disabled && list.length > 1 && <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500 shrink-0" onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>}
        </div>
      ))}
      {!disabled && list.length < 7 && <Button variant="outline" size="sm" onClick={() => onChange([...list, ""])} data-testid="why-add"><Plus className="w-3.5 h-3.5 mr-1" />Ask why again</Button>}
    </div>
  );
}

export function CategorySelect({ value, onChange, disabled }) {
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)} disabled={disabled}>
      <SelectTrigger data-testid="rca-category-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Select category</SelectItem>
        {Object.entries(RCA_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function ActionsEditor({ value = [], onChange, staff, disabled }) {
  const upd = (id, k, v) => onChange(value.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-3" data-testid="rca-actions">
      {value.map((a) => (
        <div key={a.id} className={`border rounded-lg p-3 space-y-2 ${a.overdue ? "border-red-200 bg-red-50/40" : "border-slate-200"}`} data-testid={`action-item-${a.id}`}>
          <div className="flex gap-2 items-start">
            <Input value={a.title} disabled={disabled} onChange={(e) => upd(a.id, "title", e.target.value)} placeholder="Action to prevent recurrence" className="h-9 flex-1" data-testid="action-title-input" />
            {!disabled && <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500 shrink-0" onClick={() => onChange(value.filter((x) => x.id !== a.id))} data-testid="action-remove"><Trash2 className="w-4 h-4" /></Button>}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Select value={a.type} onValueChange={(v) => upd(a.id, "type", v)} disabled={disabled}>
              <SelectTrigger className="h-9" data-testid="action-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="corrective">Corrective</SelectItem><SelectItem value="preventive">Preventive</SelectItem></SelectContent>
            </Select>
            <Select value={a.owner_id || "none"} onValueChange={(v) => upd(a.id, "owner_id", v === "none" ? null : v)} disabled={disabled}>
              <SelectTrigger className="h-9" data-testid="action-owner-select"><SelectValue placeholder="Owner" /></SelectTrigger>
              <SelectContent><SelectItem value="none">No owner</SelectItem>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={a.due_date || ""} disabled={disabled} onChange={(e) => upd(a.id, "due_date", e.target.value || null)} className="h-9" data-testid="action-due-input" />
            <Select value={a.status} onValueChange={(v) => upd(a.id, "status", v)} disabled={disabled}>
              <SelectTrigger className={`h-9 ${ACTION_STATUS[a.status]?.chip}`} data-testid="action-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ACTION_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {a.overdue && <p className="text-[11px] font-semibold text-red-600 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Overdue</p>}
        </div>
      ))}
      {!value.length && <p className="text-sm text-slate-400">No actions yet. Every RCA needs at least one action.</p>}
      {!disabled && <Button variant="outline" size="sm" onClick={() => onChange([...value, { id: newId(), title: "", type: "corrective", owner_id: null, due_date: null, status: "open" }])} data-testid="action-add"><Plus className="w-3.5 h-3.5 mr-1" />Add action</Button>}
    </div>
  );
}

export function TextField({ label, hint, value, onChange, disabled, placeholder, testid, rows = "min-h-[80px]" }) {
  return (
    <Field label={label} hint={hint}>
      {disabled ? <p className="text-sm text-slate-700 whitespace-pre-wrap">{value || "—"}</p> : (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`text-sm ${rows}`} data-testid={testid} />
      )}
    </Field>
  );
}
